import { useState } from 'react';

import { trackEvent } from '@/lib/mixpanel';
import { DomainError } from '@/types/data-source';
import { useAtomValue } from 'jotai';

import { dealAtom } from '../../states/deal.state';
import {
  CsvDataSource,
  useAddCouponCodesCsvDataSourceToDealMutation,
  useGetSignedUrlLazyQuery,
  useMarkDataSourceAsImportedMutation,
} from '../graphql/generated/graphql';

const upload = async (
  file: File,
  signedUrl: string
): Promise<{ ok: true } | { ok: false; message: string }> => {
  const response = await fetch(signedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
      'Cache-Control': 'no-cache',
    },
    body: file,
  });
  if (!response.ok) return { ok: false, message: 'Failed to upload file' };
  return { ok: true };
};

const useWatchDataSourceCsvProcessed = (): CsvDataSource[] => {
  const deal = useAtomValue(dealAtom);
  return (
    deal?.dataSources.filter(
      (ds): ds is CsvDataSource => ds.__typename === 'CSVDataSource'
    ) ?? []
  );
};

/**
 * Custom hook for uploading coupon codes CSV files
 * Uses the dedicated mutation for coupon codes data source
 */
export const useUploadCouponCodesCsv = (dealId: string) => {
  const [dataSourceId, setDataSourceId] = useState<string | null>(null);
  const [domainError, setDomainError] = useState<DomainError | null>(null);

  const dataSources = useWatchDataSourceCsvProcessed();

  const [addCouponCodesCsvDataSourceToDeal] =
    useAddCouponCodesCsvDataSourceToDealMutation();

  const [getSignedUrlAsync, { data, loading, error }] =
    useGetSignedUrlLazyQuery({
      variables: { dealId },
      fetchPolicy: 'no-cache',
    });

  const [markDataSourceAsImported] = useMarkDataSourceAsImportedMutation();

  const mutation = async (
    file: File
  ): Promise<
    { success: true } | { success: false; domainError: DomainError }
  > => {
    setDomainError(null);
    trackEvent('IMPORT_STARTED', { dealId, source: 'couponCsv' });

    const trackFailure = (errorCode: string, step: string) => {
      trackEvent('IMPORT_FAILED', {
        dealId,
        source: 'couponCsv',
        errorCode,
        step,
      });
    };

    let currentDataSourceId: string;

    const { data: addCouponCodesCsvData } =
      await addCouponCodesCsvDataSourceToDeal({
        variables: {
          dealId,
        },
      });

    const result = addCouponCodesCsvData?.addCouponCodesCsvDataSourceToDeal;

    if (result?.__typename === 'MutationSuccess') {
      currentDataSourceId = result.id;
      setDataSourceId(currentDataSourceId);
    } else if (result?.__typename === 'DomainException') {
      const err = {
        code: result.code,
        message: `${result.code}`,
      };
      setDomainError(err);
      trackFailure(err.code, 'addCouponCodesCsvDataSource');
      return { success: false, domainError: err };
    } else {
      const err = {
        code: 'IMPORT_FAILED',
        message: 'Unknown mutation result',
      };
      setDomainError(err);
      trackFailure(err.code, 'addCouponCodesCsvDataSource');
      return { success: false, domainError: err };
    }

    const { data: signedUrlData } = await getSignedUrlAsync();

    const csvDataSourceItem = signedUrlData?.deal?.dataSources.find(
      (dataSource): dataSource is CsvDataSource =>
        dataSource?.__typename === 'CSVDataSource' &&
        dataSource.id === currentDataSourceId
    );

    if (!csvDataSourceItem) {
      const err = {
        code: 'IMPORT_FAILED',
        message: 'CSV data source not found after creation',
      };
      setDomainError(err);
      trackFailure(err.code, 'getSignedUrl');
      return { success: false, domainError: err };
    }

    if (!csvDataSourceItem.signedUrl) {
      const err = {
        code: 'IMPORT_FAILED',
        message: 'No signed URL found',
      };
      setDomainError(err);
      trackFailure(err.code, 'getSignedUrl');
      return { success: false, domainError: err };
    }

    const uploadResult = await upload(file, csvDataSourceItem.signedUrl);
    if (!uploadResult.ok) {
      const err = {
        code: 'IMPORT_FAILED',
        message: uploadResult.message,
      };
      setDomainError(err);
      trackFailure(err.code, 'upload');
      return { success: false, domainError: err };
    }

    const { data: markData } = await markDataSourceAsImported({
      variables: {
        dealId,
        dataSourceId: currentDataSourceId,
      },
    });

    const markResult = markData?.markDataSourceAsImported;
    if (markResult?.__typename === 'DomainException') {
      const err = {
        code: markResult.code,
        message: `${markResult.code}`,
      };
      setDomainError(err);
      trackFailure(err.code, 'markAsImported');
      return { success: false, domainError: err };
    }

    trackEvent('IMPORT_COMPLETED', {
      dealId,
      source: 'couponCsv',
    });
    return { success: true };
  };

  const currentDataSource = dataSources.find((ds) => ds.id === dataSourceId);

  const dataSourceStatus = currentDataSource?.status;

  return {
    data,
    loading,
    errors:
      currentDataSource?.errors && currentDataSource.errors.length > 0
        ? currentDataSource.errors.map((e) => e.message).join('\n')
        : error,
    addCouponCodesCsvDataSourceToDeal: mutation,
    dataSourceStatus,
    domainError,
    isDomainError: Boolean(domainError),
  };
};
